import React from 'react';
import { Globe, Award, TrendingUp, Users } from 'lucide-react';

export const AboutOSSD = () => {
  return (
    <section id="about-ossd" className="py-24 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-block">
                <span className="text-amber-600 font-semibold text-sm tracking-wider uppercase bg-amber-100 px-4 py-2 rounded-full border border-amber-200">
                  Understanding OSSD
                </span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                What is <span className="text-blue-600">OSSD</span>?
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                The Ontario Secondary School Diploma (OSSD) is a highly respected and globally recognized high school diploma awarded by the Ministry of Education in Ontario, Canada. It provides students with a strong foundation for post-secondary education and career success.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                The OSSD has consistently ranked in the <span className="font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded">TOP 5 in the world</span> by the Programme for International Student Assessment (PISA), showcasing its academic rigor and excellence.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-6 pt-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-blue-100 hover:border-blue-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Globe className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Globally Recognized</h3>
                    <p className="text-sm text-gray-600">Accepted by universities worldwide and ranked Top 5 by OECD-PISA</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-blue-100 hover:border-blue-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Ministry Approved</h3>
                    <p className="text-sm text-gray-600">Official qualification from Ontario Ministry of Education</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-blue-100 hover:border-blue-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Comprehensive Curriculum</h3>
                    <p className="text-sm text-gray-600">Well-rounded education covering English, Math, Science, and more</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-blue-100 hover:border-blue-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Direct University Access</h3>
                    <p className="text-sm text-gray-600">No SAT/ACT required for Canadian universities</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Requirements */}
          <div className="relative">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 text-white shadow-2xl">
              <h3 className="text-2xl font-bold mb-6">OSSD Requirements</h3>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center text-blue-900 font-bold text-lg shadow-lg">30</div>
                    <h4 className="font-bold text-lg">Credits Required</h4>
                  </div>
                  <p className="text-blue-100 ml-15">18 compulsory credits + 12 optional credits</p>
                </div>

                <div>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center text-blue-900 font-bold text-lg shadow-lg">✓</div>
                    <h4 className="font-bold text-lg">Literacy Requirement</h4>
                  </div>
                  <p className="text-blue-100 ml-15">Pass the Ontario Secondary School Literacy Test (OSSLT) or complete OLC4O course</p>
                </div>

                <div>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center text-blue-900 font-bold text-lg shadow-lg">40</div>
                    <h4 className="font-bold text-lg">Community Hours</h4>
                  </div>
                  <p className="text-blue-100 ml-15">Complete 40 hours of community involvement activities</p>
                </div>
              </div>

              <div className="mt-8 bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
                <p className="text-amber-300 font-medium mb-2">Recognition by OECD</p>
                <p className="text-blue-100 text-sm">The Organisation for Economic Co-operation and Development (OECD) recognizes OSSD as one of the world's leading secondary school qualifications.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
